import type { ADFEntity } from '@atlaskit/adf-utils/types';

export type UserInfo = {
	accountId: string;
	name: string;
};

export enum SnippetBodyFormat {
	ADF = 'ADF',
}

export enum SnippetProduct {
	CONFLUENCE = 'CONFLUENCE',
	JIRA = 'JIRA',
}

export enum SnippetScope {
	GLOBAL = 'GLOBAL',
	SITE = 'SITE',
	USER = 'USER',
}

export enum SnippetStatus {
	ACTIVE = 'active',
	DELETED = 'deleted',
}

/**
 * Provenance of a template, used for analytics to split usage by source.
 * - `ATLASSIAN`: Atlassian-provided out-of-the-box (OOTB) template (GLOBAL scope).
 * - `USER`: user/space-created template (USER or SITE scope).
 */
export enum TemplateType {
	ATLASSIAN = 'atlassian',
	USER = 'user',
}

/**
 * Derives the template provenance from its scope.
 *
 * GLOBAL-scope snippets are the locale-aware, Atlassian-provided (OOTB) templates;
 * USER and SITE scopes are user/space-created. If the backend later returns an
 * explicit provenance field on `SnippetData`, prefer that over this derivation.
 */
export const getTemplateTypeFromScope = (scope: SnippetScope): TemplateType =>
	scope === SnippetScope.GLOBAL ? TemplateType.ATLASSIAN : TemplateType.USER;

export type SnippetData = {
	body: string;
	bodyFormat: SnippetBodyFormat;
	createdAt: string; // String of integer timestamp
	createdBy: string;
	description?: string; // User defined name
	insertCount?: number;
	isFavorite: boolean;
	/** When true, template is shared with the site. Returned by BE for USER and SITE scope; may be omitted for GLOBAL */
	isShared?: boolean;
	label?: string;
	name: string; // User defined name
	product: SnippetProduct;
	resourceId: string; // ARI of the Snippet
	scope: SnippetScope;
	shareId?: string;
	snippetId: string;
	status: SnippetStatus;
	/**
	 * Template provenance, when populated by the backend. When omitted, consumers
	 * derive it from `scope` via `getTemplateTypeFromScope`.
	 */
	templateType?: TemplateType;
	updatedAt: string; // String of integer timestamp
};

export type Snippet = Omit<SnippetData, 'body' | 'templateType'> & {
	body: ADFEntity;
	/**
	 * Template provenance (atlassian | user). Re-declared here (rather than inherited from
	 * `SnippetData`) to document transform-layer semantics: it is populated on transformed
	 * snippets only when the `platform_editor_blocks_patch_2` feature gate is enabled, and is
	 * otherwise left undefined.
	 */
	templateType?: TemplateType;
};

export type SnippetsProvider = {
	getSnippets: () => Promise<Snippet[]>;
};
